import {BlockMap} from 'UI/modules/Blocks';

import {isDefined} from 'Application/utils/typeGuards';

import type {Config} from 'types/payload-types';

export type BlockUnionType = Config['blocks'][keyof Config['blocks']];

/**
 * A custom React hook that maps CMS-provided block content to their corresponding React components.
 * This hook processes an array of content blocks—each with a `blockType` and associated data—and transforms them
 * into renderable structures by resolving the appropriate component from the `BlockMap`. It omits the `blockType`
 * property from the final props and returns each block paired with its component and cleaned data. If no content is defined,
 * the hook returns `undefined` for the block list, allowing consuming components to safely render conditionally.
 *
 * @param content An array of `HeroBlockType` entries representing structured CMS content or `null`/`undefined`.
 * @returns An object containing the `blocks` array, where each entry includes a `Component` and its corresponding `data`.
 *
 * @example
 * ```tsx
 * const { blocks } = useBlockRenderer(page.blocks);
 * return blocks?.map(({ Component, data }) => <Component key={data.id} {...data} />);
 * ```
 */
export const useBlockRenderer = (content: (BlockUnionType)[] | null | undefined) => {
    let blocks;

    if (isDefined(content)) {
        blocks = content.map(block => {
            const {blockType, ...data} = block;

            return {
                Component: BlockMap[blockType],
                data
            };
        });
    }

    return {blocks};
};